import { useMutation, useQueryClient } from '@tanstack/react-query'

import queries from '@/modules/ecom-policy/queries'
import ecomPolicyResources from '@/modules/ecom-policy/resources'
import type { PolicyExtended } from '@/modules/ecom-policy/types'
import { commonQueries } from '@/resources/queries'

import { usePolicyId } from '../hooks'

export const useChangePolicySettings = ({ onError }: { onError: () => void }) => {
  const queryClient = useQueryClient()
  const policyId = usePolicyId()

  const { mutate, isLoading } = useMutation(ecomPolicyResources.postPolicy, {
    async onMutate({ payload: policySettingsPayload }) {
      const queryKey = queries.policyDetails(policyId)

      // Snapshot the previous value
      const previousPolicySettings = queryClient.getQueryData<{
        response: PolicyExtended
      }>(queryKey)

      // Optimistically update to the new value
      queryClient.setQueryData(queryKey, {
        ...previousPolicySettings,
        response: { ...previousPolicySettings?.response, ...policySettingsPayload },
      })

      // Return a context with the previous values
      return previousPolicySettings
    },
    // If the mutation fails, use the context we returned above
    onError(_err, _selectProductPayload, previousPolicySettings) {
      onError()
      queryClient.setQueryData(queries.policyDetails(policyId), previousPolicySettings)
    },
    onSettled() {
      queryClient.invalidateQueries(queries.policyDetails(policyId))
      queryClient.invalidateQueries(commonQueries.policyList(), { refetchType: 'all' })
    },
  })

  return { changePolicySettings: mutate, isSaving: isLoading }
}
